// ConnectionContext — single health model for the agent workspace. Combines
// THREE independent signals (reported by the softphone every heartbeat tick):
//   * backendOk   — the /me/heartbeat request succeeded.
//   * asteriskOk  — backend says the live ARI link to Asterisk is up
//                   (null in simulator / not-yet-observed → ignored).
//   * sipOk        — the agent's SIP endpoint is registered to Asterisk
//                   (null before first registration / simulator → ignored).
//
// If ANY of these is down for >= FAIL_THRESHOLD consecutive checks, the agent
// can't reliably take calls → show a banner + block dialing. This is what makes
// "Asterisk stopped but backend still up" produce a visible alert (previously
// it silently showed the softphone offline with no intimation).
//
// When everything recovers AFTER a real outage, `sessionInterrupted` flips true
// so the workspace asks the agent to log out and back in (a fresh page load
// rebuilds the SIP stack + re-parks the off-hook channel cleanly).
import { createContext, useCallback, useContext, useRef, useState } from "react";

const ConnectionCtx = createContext(null);

const FAIL_THRESHOLD = 2;   // ~10-15s at the 5s heartbeat cadence

export function ConnectionProvider({ children }) {
  const [online, setOnline] = useState(true);
  const [reason, setReason] = useState("");        // "backend" | "telephony" | ""
  const [sessionInterrupted, setSessionInterrupted] = useState(false);
  const failRef = useRef(0);
  const onlineRef = useRef(true);

  const reportHealth = useCallback(({ backendOk = true, asteriskOk, sipOk } = {}) => {
    // null/undefined signals are "not applicable" → treated as healthy.
    const telephonyDown = asteriskOk === false || sipOk === false;
    const healthy = backendOk && !telephonyDown;
    if (healthy) {
      failRef.current = 0;
      if (onlineRef.current === false) {
        onlineRef.current = true;
        setOnline(true);
        setReason("");
        setSessionInterrupted(true);   // recovered from a real outage → re-login
      }
    } else {
      failRef.current += 1;
      if (failRef.current >= FAIL_THRESHOLD && onlineRef.current) {
        onlineRef.current = false;
        setOnline(false);
        setReason(!backendOk ? "backend" : "telephony");
      }
    }
  }, []);

  return (
    <ConnectionCtx.Provider value={{
      online, reason, sessionInterrupted, setSessionInterrupted, reportHealth,
    }}>
      {children}
    </ConnectionCtx.Provider>
  );
}

export function useConnection() {
  const v = useContext(ConnectionCtx);
  return v || {
    online: true, reason: "", sessionInterrupted: false,
    setSessionInterrupted: () => {}, reportHealth: () => {},
  };
}
